import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { ASSETS, BRAND } from "@/lib/brand";

const MAISON_ITEMS = [
  { id: "filosofia", label: "Filosofia" },
  { id: "artigianalita", label: "Artigianalità" },
  { id: "registro", label: "Registro Globale" },
];

const CUVEE_ITEMS = [
  { id: "terra-arsa", label: "Terra Arsa", sub: "Nero d'Avola DOC", color: "#63171B" },
  { id: "scilla", label: "Scilla", sub: "Inzolia IGT", color: "#0A0A0A" },
  { id: "blanche", label: "Blanche", sub: "Blanc de Noirs 1.333", color: "#8a6d3b" },
  { id: "claire", label: "Claire", sub: "Rosato Micro-lotto", color: "#B76E79" },
];

function Dropdown({ label, testid, items, go, wine }) {
  const [openDd, setOpenDd] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpenDd(true)}
      onMouseLeave={() => setOpenDd(false)}
    >
      <button
        data-testid={testid}
        className="overline hover:text-[#0A0A0A] transition-colors text-[10.5px] whitespace-nowrap flex items-center gap-1.5 py-2"
        aria-haspopup="true"
        aria-expanded={openDd}
      >
        {label}
        <ChevronDown
          size={12}
          className={`transition-transform duration-300 ${openDd ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {openDd && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-2"
            data-testid={`${testid}-menu`}
          >
            <div className="bg-white border border-[#EAEAEA] shadow-[0_18px_50px_rgba(10,10,10,0.10)] py-3 min-w-[240px]">
              {items.map((it) => (
                <button
                  key={it.id}
                  data-testid={`nav-dd-${it.id}`}
                  onClick={() => { setOpenDd(false); go(it.id); }}
                  className="w-full text-left px-6 py-2.5 hover:bg-[#FAF7F0] transition-colors group/item"
                >
                  <span
                    className={wine ? "font-serif-display italic text-[15px]" : "overline text-[10px] text-[#3D3D3D] group-hover/item:text-[#0A0A0A]"}
                    style={wine ? { color: it.color } : undefined}
                  >
                    {it.label}
                  </span>
                  {it.sub && (
                    <span className="block overline text-[8.5px] text-[#9A9484] mt-0.5">{it.sub}</span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const wasOpen = open;
    setOpen(false);
    // Su mobile attende la chiusura del menu (l'animazione altera l'altezza
    // del documento e interrompe lo smooth scroll appena avviato)
    const delay = wasOpen ? 460 : 0;
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 650);
      }
    }, delay);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-md border-b border-[#EAEAEA]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between">
        <button
          onClick={() => go("hero")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group shrink-0"
          aria-label="Vai a inizio pagina"
        >
          <img
            src={ASSETS.logo}
            alt="La Valle degli Iblei stemma"
            className="h-10 w-10 object-contain"
          />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-[15px] tracking-mid text-[#0A0A0A]">
              La Valle degli Iblei
            </span>
            <span className="overline text-[9px] mt-0.5">Selezione d&apos;Autore</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          <Dropdown label="La Maison" testid="nav-maison" items={MAISON_ITEMS} go={go} />
          <Dropdown label="Cuvée d'Autore" testid="nav-cuvee" items={CUVEE_ITEMS} go={go} wine />
          <button
            data-testid="nav-sommelier-club"
            onClick={() => go("sommelier-club")}
            className="overline hover:text-[#0A0A0A] transition-colors text-[10.5px] whitespace-nowrap"
          >
            Sommelier Club
          </button>
          <button
            data-testid="nav-contatti"
            onClick={() => go("contatti")}
            className="overline hover:text-[#0A0A0A] transition-colors text-[10.5px] whitespace-nowrap"
          >
            Contatti
          </button>
          <button
            data-testid="nav-cta"
            onClick={() => go("contatti")}
            className="btn-ghost whitespace-nowrap"
            style={{ fontSize: "10.5px" }}
          >
            Richiedi Allocazione
            <span aria-hidden>→</span>
          </button>
        </nav>

        <button
          className="md:hidden p-2"
          aria-label="Apri menu"
          data-testid="mobile-menu-btn"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="md:hidden bg-white border-t border-[#EAEAEA] overflow-hidden"
          >
            <div className="px-8 py-8 flex flex-col gap-7 max-h-[78vh] overflow-y-auto">
              <div>
                <p className="overline text-[9px] text-[#9A9484] mb-3">La Maison</p>
                <div className="flex flex-col gap-3">
                  {MAISON_ITEMS.map((n) => (
                    <button
                      key={n.id}
                      data-testid={`nav-mobile-${n.id}`}
                      onClick={() => go(n.id)}
                      className="text-left font-serif-display text-xl text-[#0A0A0A]"
                    >
                      {n.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline text-[9px] text-[#9A9484] mb-3">Cuvée d&apos;Autore</p>
                <div className="flex flex-col gap-3.5">
                  {CUVEE_ITEMS.map((n) => (
                    <button
                      key={n.id}
                      data-testid={`nav-mobile-${n.id}`}
                      onClick={() => go(n.id)}
                      className="text-left"
                    >
                      <span className="font-serif-display italic text-xl" style={{ color: n.color }}>
                        {n.label}
                      </span>
                      <span className="ml-3 overline text-[8.5px] text-[#9A9484]">{n.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="hairline" />
              <button
                data-testid="nav-mobile-sommelier-club"
                onClick={() => go("sommelier-club")}
                className="text-left font-serif-display text-xl text-[#0A0A0A]"
              >
                Sommelier Club
              </button>
              <button
                data-testid="nav-mobile-contatti"
                onClick={() => go("contatti")}
                className="text-left font-serif-display text-xl text-[#0A0A0A]"
              >
                Contatti
              </button>
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go("contatti")}
                className="text-left font-serif-display text-xl italic"
                style={{ color: "#8B7A55" }}
              >
                Richiedi Allocazione →
              </button>
              <span className="overline text-[10px]">{BRAND.email}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
